/**
 * The objects and the events of the last day, for the Objects and Events tiles of the home
 * launcher. The metadata service gives rows, not counts, so each count is the length of one page
 * of a search: `COUNT_LIMIT` rows means "that many or more". The snapshot loads again each time
 * the launcher comes back, so the window ends at the time of the visit.
 */

import { atom } from 'jotai';

import { snapshotAtoms } from '../snapshotAtoms';
import { api } from '@/api';
import { countIntoBins } from '@/lib/timeBins';
import { DAY_MS } from '@/lib/timeUnits';
import type { ActivityCount, EventSearchResult, RecentActivitySnapshot } from '@/types/events';

/** The window the tiles report. */
const RECENT_WINDOW_MS = DAY_MS;

/** As many rows as a tile's number is worth paying for; past it the tile says `500+`. */
const COUNT_LIMIT = 500;

// The `object` prefix holds what the agents found (object.person, object.vehicle), the `event`
// prefix what they reported about a scene. Neither covers the other, and the Objects and Events
// panels scope themselves the same way.
const OBJECT_CATEGORY = 'object';
const EVENT_CATEGORY = 'event';

/** One bar an hour over the full day. */
const BIN_COUNT = 24;

/** The last day's rows, as the searches returned them. */
interface RecentEventsSnapshot {
    objects: EventSearchResult[];
    events: EventSearchResult[];
    /** When the searches ran: the end of the window. */
    untilMs: number;
    isLoading: boolean;
    error: Error | null;
}

const PENDING: RecentEventsSnapshot = {
    objects: [],
    events: [],
    untilMs: 0,
    isLoading: true,
    error: null,
};

function getCountQuery(category: string, sinceMs: number): string {
    const params = new URLSearchParams();
    params.set('limit', String(COUNT_LIMIT));
    params.set('category', category);
    params.set('utcTimeMs', String(Math.floor(sinceMs)));
    return params.toString();
}

function getActivityCount(rows: EventSearchResult[], untilMs: number): ActivityCount {
    const sinceMs = untilMs - RECENT_WINDOW_MS;
    const times = rows.map((row) => row.utcTimeMs);
    const isCapped = rows.length >= COUNT_LIMIT;
    // The page is the newest rows first, so a full one says nothing about the time before its
    // oldest row: the bins cover only what it holds.
    const fromMs = isCapped ? Math.min(...times) : sinceMs;
    return {
        count: rows.length,
        isCapped,
        bins: countIntoBins(times, fromMs, untilMs, BIN_COUNT),
        spanMs: untilMs - fromMs,
    };
}

const recentEvents = snapshotAtoms<RecentEventsSnapshot>(PENDING, async () => {
    const untilMs = Date.now();
    const sinceMs = untilMs - RECENT_WINDOW_MS;
    const [objects, events] = await Promise.all([
        api.searchEvents(getCountQuery(OBJECT_CATEGORY, sinceMs)),
        api.searchEvents(getCountQuery(EVENT_CATEGORY, sinceMs)),
    ]);
    return { objects, events, untilMs, isLoading: false, error: null };
});

/** The counts and the shape of the day that the tiles draw. */
export const recentActivityAtom = atom((get): RecentActivitySnapshot => {
    const { objects, events, untilMs, isLoading, error } = get(recentEvents.value);
    return {
        objects: getActivityCount(objects, untilMs),
        events: getActivityCount(events, untilMs),
        isLoading,
        error,
    };
});
